import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';

const STORAGE_KEY = 'ai-disclosure-seen';

/**
 * One-time disclosure modal shown the first time a user opens the AI
 * interview chat. Satisfies EU AI Act Art.50(1) — informing the user
 * they are interacting with an AI system. Acknowledgement is stored
 * in localStorage so the modal does not reappear; recurring
 * notification is provided by the persistent <AIDisclosureBadge />.
 */
export default function FirstTimeAIDisclosureModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) setOpen(true);
    } catch {
      // If storage is blocked we silently skip — nothing critical.
    }
  }, []);

  const acknowledge = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="AI disclosure"
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/50 p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          type="button"
          onClick={acknowledge}
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-orange-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            How AI works here
          </h2>
        </div>
        <ul className="text-sm text-gray-700 space-y-2 leading-relaxed mb-5">
          <li>
            • Your messages are sent to <strong>DeepInfra, Inc. (USA)</strong> for
            processing by Llama 3.3 70B.
          </li>
          <li>
            • Replies are AI-generated and may contain inaccuracies. Use them
            for practice, not for real legal, financial, or medical decisions.
          </li>
          <li>
            • Your sessions are saved to your account so you can review your
            progress.
          </li>
        </ul>
        <p className="text-xs text-gray-500 mb-5">
          Read the full{' '}
          <Link to="/ai" target="_blank" className="text-[#F47920] underline">
            AI Transparency Notice
          </Link>{' '}
          and{' '}
          <Link to="/privacy" target="_blank" className="text-[#F47920] underline">
            Privacy Policy
          </Link>
          .
        </p>
        <button
          type="button"
          onClick={acknowledge}
          className="w-full bg-[#F47920] hover:bg-[#d96a14] text-white font-semibold py-2.5 rounded-xl transition-colors"
        >
          Got it, continue
        </button>
      </div>
    </div>
  );
}
